import React from 'react';
import { Activity, BarChart2, TrendingUp, Layers, Play, Pause, RefreshCw } from 'lucide-react';

interface PlaybackControlsProps {
  isPlaying: boolean;
  playbackSpeed: number;
  onPlayPause: () => void;
  onSpeedChange: (speed: number) => void;
}

interface GaitTabsProps {
  activeTab: 'pressure' | 'asymmetry' | 'longitudinal' | 'metrics';
  onTabChange: (tab: 'pressure' | 'asymmetry' | 'longitudinal' | 'metrics') => void;
  playbackControls: PlaybackControlsProps;
  compareWithBaseline: boolean;
  onCompareToggle: () => void;
}

const GaitTabs: React.FC<GaitTabsProps> = ({
  activeTab,
  onTabChange,
  playbackControls,
  compareWithBaseline,
  onCompareToggle
}) => {
  const tabs = [
    { id: 'pressure', label: 'Pressure Mapping', icon: Layers },
    { id: 'asymmetry', label: 'Gait Asymmetry', icon: RefreshCw },
    { id: 'longitudinal', label: 'Longitudinal Analysis', icon: TrendingUp },
    { id: 'metrics', label: 'Gait Metrics', icon: BarChart2 }
  ];

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-4 border-b">
        <div className="flex flex-wrap justify-between items-center">
          {/* Tabs */}
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id as any)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 focus:outline-none whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Controls that change based on active tab */}
          <div className="flex items-center space-x-4 py-2">
            {(activeTab === 'pressure') && (
              <div className="flex items-center space-x-2 border-l pl-4">
                <button
                  onClick={playbackControls.onPlayPause}
                  className="p-1 rounded-full hover:bg-gray-100"
                  title={playbackControls.isPlaying ? 'Pause' : 'Play'}
                >
                  {playbackControls.isPlaying ? (
                    <Pause size={18} className="text-gray-600" />
                  ) : (
                    <Play size={18} className="text-gray-600" />
                  )}
                </button>
                <span className="text-sm text-gray-700">Playback:</span>
                <div className="flex space-x-1">
                  {[0.5, 1, 1.5, 2].map(speed => (
                    <button
                      key={speed}
                      onClick={() => playbackControls.onSpeedChange(speed)}
                      className={`px-2 py-1 text-xs rounded ${
                        playbackControls.playbackSpeed === speed
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(activeTab === 'asymmetry' || activeTab === 'metrics') && (
              <div className="flex items-center space-x-2 border-l pl-4">
                <span className="text-sm text-gray-700">Compare with baseline:</span>
                <button
                  onClick={onCompareToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    compareWithBaseline ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      compareWithBaseline ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GaitTabs; 